import axios from "axios";
import moment from "moment-timezone";
import {
    Fragment,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBlocker } from "react-router-dom";
import { ClipLoader } from "react-spinners";
import Config from "../../../../Config";
import {
    color_black,
    color_grey_dark,
    color_grey_light,
    color_red_default,
    color_white,
} from "../../../../constants/colors";
import { updateStudent } from "../../../../store/StudentSlice";
import {
    groupScheduleConflictsByWeekday,
} from "../../../../utilities/buildMakeSchedulePayload";
import { buildPeriodSchedulePayload } from "../../../../utilities/buildPeriodSchedulePayload";
import {
    buildLearningPeriodsDisplay,
    findActiveLearningPeriod,
    formatPeriodRangeLabel,
    scheduleItemsToFormState,
    scheduleItemsToLessonsSchedule,
} from "../../../../utilities/learningPeriodDisplayUtils";
import {
    detectActivePreset,
    durationDaysFromRange,
    endDateFromPreset,
    getPresetById,
    MAX_SCHEDULE_DURATION_DAYS,
    SCHEDULE_PERIOD_PRESETS,
} from "../../../../utilities/schedulePeriodUtils";
import {
    isScheduleSlotComplete,
    isValidScheduleSlot,
    parseScheduleSlot,
} from "../../../../utilities/scheduleSlotUtils";
import { useWindowDimensions } from "../../../../utils";
import { Button } from "../../../controls/Button/Button";
import ArrowIconDown from "../../../icons/ArrowIconDown";
import IconlyCurvedLightCalendar from "../../../icons/IconlyCurvedLightCalendar";
import {
    OnestBoldSmall,
    OnestNormalDefault,
    OnestNormalSmall,
} from "../../../styled/TextComponents";
import ModalQuestion from "../../ModalQuestion";
import ModalSimple from "../../ModalSimple";
import { ScheduleInfoHint } from "./ScheduleInfoHint";
import { styles } from "./styles";
import { WeekdayTimeSelector } from "./WeekdayTimeSelector";
import { useI18n } from "../../../../i18n/I18nProvider";

const weekDayArr = ["ПН", "ВТ", "СР", "ЧТ", "ПТ", "СБ", "ВС"];
const weekDayFullName = {
  ПН: "Понедельник",
  ВТ: "Вторник",
  СР: "Среда",
  ЧТ: "Четверг",
  ПТ: "Пятница",
  СБ: "Суббота",
  ВС: "Воскресенье",
};

const HOLIDAYS_HINT_SHORT = "Как сохранить расписание с учётом каникул";

const HOLIDAYS_HINT_STEPS = [
  "1. Первый период: укажите даты до каникул (например, 1 сентября — 20 декабря), выберите дни и время, нажмите «Сохранить».",
  "2. После каникул: нажмите «Добавить период», укажите даты возвращения (например, 10 января — 27 мая). Дни и время можно задать отдельно для этого периода.",
  "3. Чтобы укоротить период: откройте его, измените «Конец» и сохраните — лишние будущие занятия удалятся автоматически.",
];

const TIME_HINT_TITLE = "Время и списание с баланса";

const TIME_HINT_STEPS = [
  "Можно указывать любые минуты, например 18:10–18:45.",
  "Сколько уроков списать с баланса за это занятие: 1 или 2. На время в календаре не влияет.",
];

const dateInputStyles = `
    .student-schedule-date-input::-webkit-calendar-picker-indicator,
    .student-schedule-date-input::-webkit-inner-spin-button,
    .student-schedule-date-input::-webkit-clear-button,
    .student-schedule-time-input::-webkit-calendar-picker-indicator,
    .student-schedule-time-input::-webkit-inner-spin-button,
    .student-schedule-time-input::-webkit-clear-button {
        display: none !important;
        -webkit-appearance: none !important;
        opacity: 0 !important;
    }
    .student-schedule-time-input {
        user-select: none;
        -webkit-user-select: none;
        caret-color: transparent;
    }
    .student-schedule-info-btn {
        outline: none;
    }
    .student-schedule-info-btn:focus-visible {
        box-shadow: 0 0 0 2px rgba(235, 87, 87, 0.35);
        border-radius: 50%;
    }
`;

const buildScheduleSnapshot = ({
  startDate,
  endDate,
  selectedWeekdays,
  selectedTimes,
}) => {
  const timesByWeekday = selectedWeekdays.reduce((acc, weekday) => {
    const raw = selectedTimes[weekday];
    if (!raw) return acc;
    const parsed = parseScheduleSlot(raw);
    acc[weekday] = `${parsed.start}-${parsed.end}-${parsed.quantity}`;
    return acc;
  }, {});

  return {
    startDate: startDate || "",
    endDate: endDate || "",
    selectedWeekdays: [...selectedWeekdays].sort(
      (a, b) => weekDayArr.indexOf(a) - weekDayArr.indexOf(b),
    ),
    timesByWeekday,
  };
};

const getRiskyChangesSummary = (initialSnapshot, currentSnapshot, t) => {
  if (!initialSnapshot) return null;

  const removedWeekdays = initialSnapshot.selectedWeekdays.filter(
    (weekday) => !currentSnapshot.selectedWeekdays.includes(weekday),
  );

  const changedSlots = initialSnapshot.selectedWeekdays.filter(
    (weekday) =>
      currentSnapshot.selectedWeekdays.includes(weekday) &&
      initialSnapshot.timesByWeekday[weekday] !==
        currentSnapshot.timesByWeekday[weekday],
  );

  const isDateRangeNarrowed =
    (!!initialSnapshot.startDate &&
      !!currentSnapshot.startDate &&
      currentSnapshot.startDate > initialSnapshot.startDate) ||
    (!!initialSnapshot.endDate &&
      (!currentSnapshot.endDate ||
        (currentSnapshot.endDate &&
          currentSnapshot.endDate < initialSnapshot.endDate)));

  const warnings = [];
  const removedWeekdayLabels = removedWeekdays.map(
    (weekday) => weekDayFullName[weekday] || weekday,
  );
  const changedSlotLabels = changedSlots.map(
    (weekday) => weekDayFullName[weekday] || weekday,
  );

  if (removedWeekdays.length > 0) {
    warnings.push(t(
      "Удалены дни: {{days}}. Будущие занятия в эти дни будут отменены.",
      { days: removedWeekdayLabels.map(t).join(", ") },
    ));
  }

  if (changedSlots.length > 0) {
    warnings.push(t(
      "Изменено время в днях: {{days}}. Будущие занятия будут пересобраны.",
      { days: changedSlotLabels.map(t).join(", ") },
    ));
  }

  if (isDateRangeNarrowed) {
    warnings.push(
      t("Период сужен по датам. Занятия вне нового диапазона будут отменены."),
    );
  }

  if (warnings.length === 0) return null;
  return warnings.join("\n");
};

const ScheduleSection = ({ title, titleHint, children }) => (
  <section style={styles.scheduleSectionCard}>
    {title && (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <OnestBoldSmall style={styles.scheduleSectionTitle}>
          {title}
        </OnestBoldSmall>
        {titleHint}
      </div>
    )}
    {children}
  </section>
);

const ScheduleDateInput = ({
  inputRef,
  id,
  label,
  value,
  onChange,
  isPickerOpenRef,
}) => {
  const { locale } = useI18n();
  return (
  <div
    style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}
  >
    {label && <OnestBoldSmall style={{ margin: 0 }}>{label}</OnestBoldSmall>}
    <div
      style={{ position: "relative", width: "100%", boxSizing: "border-box" }}
    >
      <input
        className="student-schedule-date-input"
        ref={inputRef}
        id={id}
        lang={locale}
        type="date"
        onChange={onChange}
        value={value}
        onBlur={() => {
          if (isPickerOpenRef) {
            isPickerOpenRef.current = false;
          }
        }}
        style={{
          ...styles.timeSelect,
          width: "100%",
          boxSizing: "border-box",
          marginTop: 0,
          fontFamily: "Onest",
          outline: "none",
          appearance: "none",
          WebkitAppearance: "none",
          paddingLeft: "15px",
          paddingRight: "44px",
          fontSize: 16,
        }}
      />
      <button
        type="button"
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          backgroundColor: "transparent",
          cursor: "pointer",
          width: 24,
          height: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "none",
          padding: 0,
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          const el = inputRef.current;
          if (!el) return;

          if (isPickerOpenRef?.current) {
            el.blur?.();
            isPickerOpenRef.current = false;
            return;
          }

          el.focus?.();
          if (typeof el.showPicker === "function") {
            el.showPicker();
          } else {
            el.click?.();
          }
          if (isPickerOpenRef) {
            isPickerOpenRef.current = true;
          }
        }}
      >
        <IconlyCurvedLightCalendar stroke={color_grey_light} />
      </button>
    </div>
  </div>
  );
};

const WeekdaySelectorButtons = ({ selectedWeekdays, onToggleWeekday }) => {
  const { t } = useI18n();
  return (
  <div style={{ ...styles.weekdaySelectorContainer }}>
    {weekDayArr.map((el) => (
      <button
        key={el}
        type="button"
        onClick={() => onToggleWeekday(el)}
        style={{
          ...styles.weekDaySelectorButton,
          ...(selectedWeekdays.includes(el) &&
            styles.weekDaySelectorButtonActive),
        }}
      >
        <OnestNormalSmall>{t(el)}</OnestNormalSmall>
      </button>
    ))}
  </div>
  );
};

const PeriodPresetDropdown = ({ activePresetId, onSelectPreset }) => {
  const { t } = useI18n();
  return (
  <div>
    <OnestBoldSmall style={{ display: "block", marginBottom: 8 }}>
      {t("Быстрый выбор периода")}
    </OnestBoldSmall>
    <div style={{ position: "relative", width: "100%" }}>
      <select
        value={activePresetId || ""}
        onChange={(e) => onSelectPreset(e.target.value)}
        style={{
          ...styles.timeSelect,
          width: "100%",
          height: 40,
          backgroundColor: color_white,
          fontSize: 16,
          paddingLeft: 15,
          paddingRight: 36,
          appearance: "none",
          WebkitAppearance: "none",
          cursor: "pointer",
        }}
      >
        <option value="">{t("Свой период")}</option>
        {SCHEDULE_PERIOD_PRESETS.map((preset) => (
          <option key={preset.id} value={preset.id}>
            {t(preset.label)}
          </option>
        ))}
      </select>
      <div
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          pointerEvents: "none",
        }}
      >
        <ArrowIconDown fill="#B1B1B1" width={10} height={7} />
      </div>
    </div>
  </div>
  );
};

export const StudentScheduleEdit = () => {
  const { t } = useI18n();
  const student = useSelector((state) => state.student);
  const startDateInputRef = useRef(null);
  const endDateInputRef = useRef(null);
  const isStartDatePickerOpenRef = useRef(false);
  const isEndDatePickerOpenRef = useRef(false);
  const { width: screenWidth } = useWindowDimensions();
  const isDesktop = screenWidth > 1155;
  const isMobile = screenWidth <= 540;

  const user = useSelector((state) => state.user);
  const timeZone =
    user.time_zone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dispatch = useDispatch();

  const [isDirty, setIsDirty] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState(null);
  const [isNewPeriod, setIsNewPeriod] = useState(true);
  const [isPeriodFormOpen, setIsPeriodFormOpen] = useState(false);
  const [periodStartDate, setPeriodStartDate] = useState(() =>
    moment().tz(timeZone).format("YYYY-MM-DD"),
  );
  const [periodEndDate, setPeriodEndDate] = useState("");
  const [selectedWeekdays, setSelectedWeekdays] = useState([]);
  const [selectedTimes, setSelectedTimes] = useState({});

  const [modalContent, setModalContent] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);
  const [isConfirmDeletePeriodOpen, setIsConfirmDeletePeriodOpen] =
    useState(false);
  const [isConfirmRemoveWeekdayOpen, setIsConfirmRemoveWeekdayOpen] =
    useState(false);
  const [weekdayToRemove, setWeekdayToRemove] = useState(null);
  const [isConfirmRiskySaveOpen, setIsConfirmRiskySaveOpen] = useState(false);
  const [riskySaveSummary, setRiskySaveSummary] = useState(null);
  const [periodToDeleteId, setPeriodToDeleteId] = useState(null);
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [skippedPastTodaySlots, setSkippedPastTodaySlots] = useState([]);
  const [isCheckingConflicts, setIsCheckingConflicts] = useState(false);
  const [isConflictCheckFailed, setIsConflictCheckFailed] = useState(false);
  const [learningPeriods, setLearningPeriods] = useState([]);
  const [isLearningPeriodsLoading, setIsLearningPeriodsLoading] =
    useState(false);
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const conflictCheckRequestIdRef = useRef(0);
  const initialPeriodSnapshotRef = useRef(null);

  const startMoment = useMemo(
    () => moment(periodStartDate).tz(timeZone).startOf("day"),
    [periodStartDate, timeZone],
  );
  const endMoment = useMemo(
    () =>
      periodEndDate ? moment(periodEndDate).tz(timeZone).startOf("day") : null,
    [periodEndDate, timeZone],
  );

  const weekDuration = useMemo(() => {
    if (!endMoment) return 1;
    return durationDaysFromRange(startMoment, endMoment);
  }, [startMoment, endMoment]);

  const activePresetId = useMemo(() => {
    if (!endMoment) return null;
    return detectActivePreset(startMoment, endMoment);
  }, [startMoment, endMoment]);

  const hasScheduleConflicts = scheduleConflicts.length > 0;

  const conflictsByWeekday = useMemo(
    () => groupScheduleConflictsByWeekday(scheduleConflicts, timeZone),
    [scheduleConflicts, timeZone],
  );

  const isFormValid = useMemo(() => {
    if (!periodStartDate) return false;
    if (selectedWeekdays.length === 0) return false;
    if (
      selectedWeekdays.some(
        (weekday) =>
          !selectedTimes[weekday] ||
          !isValidScheduleSlot(selectedTimes[weekday]),
      )
    ) {
      return false;
    }
    if (endMoment && endMoment.isBefore(startMoment, "day")) return false;
    if (endMoment && weekDuration > MAX_SCHEDULE_DURATION_DAYS) return false;
    return true;
  }, [
    periodStartDate,
    selectedWeekdays,
    selectedTimes,
    endMoment,
    startMoment,
    weekDuration,
  ]);

  const canSave =
    isFormValid &&
    isDirty &&
    !isLoading &&
    !hasScheduleConflicts &&
    !isCheckingConflicts;

  const editingTitle = isNewPeriod
    ? t("Новый период обучения")
    : t("Параметры выбранного периода");

  const saveHint = useMemo(() => {
    if (canSave) return null;
    if (isLoading) return null;
    if (!isDirty) return t("Измените период, чтобы сохранить");
    if (!periodStartDate) return t("Укажите дату начала");
    if (selectedWeekdays.length === 0) return null;
    if (
      selectedWeekdays.some(
        (weekday) =>
          selectedTimes[weekday] &&
          isScheduleSlotComplete(selectedTimes[weekday]) &&
          !isValidScheduleSlot(selectedTimes[weekday]),
      )
    ) {
      return t("Укажите корректное время для выбранных дней");
    }
    if (endMoment && endMoment.isBefore(startMoment, "day")) {
      return t("Дата окончания не может быть раньше начала");
    }
    if (endMoment && weekDuration > MAX_SCHEDULE_DURATION_DAYS) {
      return t("Период не может превышать {{count}} дней", { count: MAX_SCHEDULE_DURATION_DAYS });
    }
    if (isCheckingConflicts) return t("Проверяем расписание на конфликты...");
    if (isConflictCheckFailed) {
      return t("Не удалось проверить конфликты заранее — попробуйте сохранить ещё раз или обновите страницу");
    }
    if (hasScheduleConflicts) return t("Выберите другое время — слот уже занят");
    return null;
  }, [
    canSave,
    isLoading,
    isDirty,
    periodStartDate,
    selectedWeekdays,
    selectedTimes,
    endMoment,
    startMoment,
    weekDuration,
    isCheckingConflicts,
    isConflictCheckFailed,
    hasScheduleConflicts,
    t,
  ]);

  const skippedPastTodayMessage = useMemo(() => {
    if (!skippedPastTodaySlots.length) return null;
    const preview = skippedPastTodaySlots
      .slice(0, 2)
      .map((slot) => {
        const startLabel = moment
          .utc(slot.start_time)
          .tz(timeZone)
          .format("dd HH:mm");
        const endLabel = moment.utc(slot.end_time).tz(timeZone).format("HH:mm");
        return `${startLabel}-${endLabel}`;
      })
      .join(", ");
    const rest = skippedPastTodaySlots.length - 2;
    return t("Сегодня уже прошло и будет пропущено: {{preview}}{{rest}}.", {
      preview,
      rest: rest > 0 ? t(" и ещё {{count}}", { count: rest }) : "",
    });
  }, [skippedPastTodaySlots, timeZone, t]);

  const syncDisplaySchedule = useCallback(
    (periods) => {
      const active = findActiveLearningPeriod(periods, timeZone);
      const learningPeriodsDisplay = buildLearningPeriodsDisplay(
        periods,
        timeZone,
        moment(),
        t,
      );
      dispatch(
        updateStudent({
          learningPeriodsDisplay,
          lessonsSchedule: active?.schedule_items?.length
            ? scheduleItemsToLessonsSchedule(active.schedule_items, timeZone, t)
            : [],
          learningPeriodLabel: formatPeriodRangeLabel(active, timeZone, t),
        }),
      );
    },
    [dispatch, timeZone, t],
  );

  const loadPeriodIntoForm = useCallback((period) => {
    const { selectedWeekdays: weekdays, selectedTimes: times } =
      scheduleItemsToFormState(period.schedule_items || []);
    setPeriodStartDate(period.start_date);
    setPeriodEndDate(period.end_date || "");
    setSelectedWeekdays(weekdays);
    setSelectedTimes(times);
    setEditingPeriodId(period.id);
    setIsNewPeriod(false);
    setIsDirty(false);
    setScheduleConflicts([]);
    setSkippedPastTodaySlots([]);
    initialPeriodSnapshotRef.current = buildScheduleSnapshot({
      startDate: period.start_date,
      endDate: period.end_date || "",
      selectedWeekdays: weekdays,
      selectedTimes: times,
    });
  }, []);

  const startNewPeriod = useCallback(() => {
    setEditingPeriodId(null);
    setIsNewPeriod(true);
    setPeriodStartDate(moment().tz(timeZone).format("YYYY-MM-DD"));
    setPeriodEndDate("");
    setSelectedWeekdays([]);
    setSelectedTimes({});
    setIsDirty(false);
    setScheduleConflicts([]);
    setSkippedPastTodaySlots([]);
    initialPeriodSnapshotRef.current = null;
  }, [timeZone]);

  const loadLearningPeriods = useCallback(async () => {
    if (student.studentId === -1) {
      setLearningPeriods([]);
      syncDisplaySchedule([]);
      return [];
    }
    setIsLearningPeriodsLoading(true);
    try {
      const { data } = await axios.get(
        `${Config.BACKEND_ADDR}/learning-periods/students/${student.studentId}`,
      );
      const periods = data || [];
      setLearningPeriods(periods);
      syncDisplaySchedule(periods);
      return periods;
    } catch (err) {
      console.warn("Failed to load learning periods", err);
      return [];
    } finally {
      setIsLearningPeriodsLoading(false);
    }
  }, [student.studentId, syncDisplaySchedule]);

  useEffect(() => {
    setHasInitializedSelection(false);
  }, [student.studentId]);

  useEffect(() => {
    loadLearningPeriods().then((periods) => {
      if (hasInitializedSelection) return;
      if (periods.length) {
        // Do not auto-select an existing period on first open.
        // Red highlight should appear only after explicit user click.
        startNewPeriod();
        setIsPeriodFormOpen(false);
      } else {
        startNewPeriod();
        setIsPeriodFormOpen(false);
      }
      setHasInitializedSelection(true);
    });
  }, [
    student.studentId,
    hasInitializedSelection,
    loadLearningPeriods,
    loadPeriodIntoForm,
    startNewPeriod,
    timeZone,
  ]);

  useEffect(() => {
    if (!isFormValid || student.studentId === -1) {
      setScheduleConflicts([]);
      setSkippedPastTodaySlots([]);
      setIsCheckingConflicts(false);
      setIsConflictCheckFailed(false);
      return undefined;
    }

    const requestId = conflictCheckRequestIdRef.current + 1;
    conflictCheckRequestIdRef.current = requestId;

    const timer = setTimeout(async () => {
      setIsCheckingConflicts(true);
      try {
        const payload = buildPeriodSchedulePayload({
          startDate: periodStartDate,
          endDate: periodEndDate || null,
          selectedWeekdays,
          selectedTimes,
          timeZone,
          periodId: editingPeriodId,
        });
        const { data } = await axios.post(
          `${Config.BACKEND_ADDR}/learning-periods/students/${student.studentId}/check`,
          payload,
        );
        if (conflictCheckRequestIdRef.current === requestId) {
          setScheduleConflicts(data.conflicts || []);
          setSkippedPastTodaySlots(data.skipped_past_slots || []);
          setIsConflictCheckFailed(false);
        }
      } catch (err) {
        if (conflictCheckRequestIdRef.current === requestId) {
          setScheduleConflicts([]);
          setSkippedPastTodaySlots([]);
          setIsConflictCheckFailed(true);
        }
        console.warn("Schedule conflict check failed:", err);
      } finally {
        if (conflictCheckRequestIdRef.current === requestId) {
          setIsCheckingConflicts(false);
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [
    isFormValid,
    student.studentId,
    selectedWeekdays,
    selectedTimes,
    periodStartDate,
    periodEndDate,
    timeZone,
    editingPeriodId,
  ]);

  const markDirty = () => setIsDirty(true);

  const shouldBlockNavigation = isDirty && isPeriodFormOpen;

  const handleSelectPeriod = (period) => {
    if (isDirty) {
      const confirmSwitch = window.confirm(
        t("Есть несохранённые изменения. Переключить период без сохранения?"),
      );
      if (!confirmSwitch) return;
    }
    loadPeriodIntoForm(period);
    setIsPeriodFormOpen(true);
  };

  const handleClosePeriodForm = useCallback(() => {
    if (isDirty) {
      const confirmClose = window.confirm(
        t("Есть несохранённые изменения. Свернуть форму без сохранения?"),
      );
      if (!confirmClose) return;
    }
    // Closing the form should also clear active period highlight.
    startNewPeriod();
    setIsPeriodFormOpen(false);
  }, [isDirty, startNewPeriod, t]);

  const askDeleteLearningPeriod = (periodId, event) => {
    event?.stopPropagation();
    setPeriodToDeleteId(periodId);
    setIsConfirmDeletePeriodOpen(true);
  };

  const handleDeleteLearningPeriod = async (isYes) => {
    setIsConfirmDeletePeriodOpen(false);
    if (!isYes || !periodToDeleteId) {
      setPeriodToDeleteId(null);
      return;
    }
    try {
      await axios.delete(
        `${Config.BACKEND_ADDR}/learning-periods/${periodToDeleteId}`,
      );
      const periods = await loadLearningPeriods();
      if (periodToDeleteId === editingPeriodId) {
        if (periods.length) {
          loadPeriodIntoForm(findActiveLearningPeriod(periods, timeZone));
        } else {
          startNewPeriod();
        }
      }
    } catch (err) {
      setModalTitle(t("Ошибка удаления периода"));
      setModalContent(
        err?.response?.data?.detail || t("Не удалось удалить период обучения"),
      );
    } finally {
      setPeriodToDeleteId(null);
    }
  };

  const handleStartDateChange = (e) => {
    markDirty();
    setPeriodStartDate(e.target.value);
    isStartDatePickerOpenRef.current = false;

    if (endMoment && moment(e.target.value).isAfter(endMoment, "day")) {
      setPeriodEndDate(e.target.value);
    }
  };

  const handleEndDateChange = (e) => {
    markDirty();
    setPeriodEndDate(e.target.value);
    isEndDatePickerOpenRef.current = false;
  };

  const handlePresetSelect = (presetId) => {
    if (!presetId) {
      if (!periodEndDate) return;
      markDirty();
      setPeriodEndDate("");
      return;
    }
    const preset = getPresetById(presetId);
    if (!preset) return;
    markDirty();
    setPeriodEndDate(
      endDateFromPreset(startMoment, preset).format("YYYY-MM-DD"),
    );
  };

  const removeWeekdayFromSchedule = (weekday) => {
    markDirty();
    setSelectedTimes((current) => {
      const next = { ...current };
      delete next[weekday];
      return next;
    });
    setSelectedWeekdays((prev) => prev.filter((wd) => wd !== weekday));
  };

  const handleConfirmRemoveWeekday = (isYes) => {
    setIsConfirmRemoveWeekdayOpen(false);
    if (!isYes || !weekdayToRemove) {
      setWeekdayToRemove(null);
      return;
    }
    removeWeekdayFromSchedule(weekdayToRemove);
    setWeekdayToRemove(null);
  };

  const handleToggleWeekday = (weekday) => {
    const isRemoving = selectedWeekdays.includes(weekday);

    if (isRemoving) {
      if (!isNewPeriod) {
        setWeekdayToRemove(weekday);
        setIsConfirmRemoveWeekdayOpen(true);
        return;
      }
      removeWeekdayFromSchedule(weekday);
      return;
    }

    markDirty();
    setSelectedTimes((current) => ({
      ...current,
      // For a new period we start with an "empty" time; conflicts should not be checked
      // until the teacher explicitly chooses start/end.
      [weekday]: "",
    }));
    setSelectedWeekdays((prev) =>
      [...prev, weekday].sort(
        (a, b) => weekDayArr.indexOf(a) - weekDayArr.indexOf(b),
      ),
    );
  };

  const handleTimeChange = (weekday, time) => {
    markDirty();
    setSelectedTimes((prev) => ({
      ...prev,
      [weekday]: time,
    }));
  };

  const savePeriod = async () => {
    setIsLoading(true);

    try {
      const payload = buildPeriodSchedulePayload({
        startDate: periodStartDate,
        endDate: periodEndDate || null,
        selectedWeekdays,
        selectedTimes,
        timeZone,
      });

      const response = isNewPeriod
        ? await axios.post(
            `${Config.BACKEND_ADDR}/learning-periods/students/${student.studentId}`,
            payload,
          )
        : await axios.patch(
            `${Config.BACKEND_ADDR}/learning-periods/${editingPeriodId}`,
            payload,
          );

      const savedPeriod = response.data.period;
      const skippedSlots = response.data.skipped_past_slots || [];
      const periods = await loadLearningPeriods();
      loadPeriodIntoForm(
        periods.find((p) => p.id === savedPeriod.id) || savedPeriod,
      );
      setIsPeriodFormOpen(false);
      syncDisplaySchedule(periods);

      setIsDirty(false);
      setScheduleConflicts([]);
      setSkippedPastTodaySlots(skippedSlots);

      if (skippedSlots.length > 0) {
        const preview = skippedSlots
          .slice(0, 2)
          .map((slot) => {
            const startLabel = moment
              .utc(slot.start_time)
              .tz(timeZone)
              .format("dd HH:mm");
            const endLabel = moment
              .utc(slot.end_time)
              .tz(timeZone)
              .format("HH:mm");
            return `${startLabel}-${endLabel}`;
          })
          .join(", ");
        const rest = skippedSlots.length - 2;
        setModalTitle(t("Период сохранён с предупреждением"));
        setModalContent(
          t("Часть занятий на сегодня уже прошла и не была создана: {{preview}}{{rest}}.", {
            preview,
            rest: rest > 0 ? t(" и ещё {{count}}", { count: rest }) : "",
          }),
        );
      }
    } catch (err) {
      if (err.response && err.response.status === 409) {
        const rawDetail = err.response?.data?.detail;
        const parsedConflictDate = moment.utc(rawDetail).tz(timeZone);
        const conflictDateText = parsedConflictDate.isValid()
          ? parsedConflictDate.format("DD.MM.YYYY HH:mm")
          : t("выбранное время");
        setModalTitle(t("Нельзя внести изменения"));
        setModalContent(
          t("У вас уже есть уроки в это время ({{date}}). Пожалуйста, проверьте расписание и выберите другой слот.", { date: conflictDateText }),
        );
      } else if (err.response?.data?.detail) {
        setModalTitle(t("Ошибка"));
        setModalContent(
          typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : t("Ошибка сохранения периода"),
        );
      } else {
        setModalContent(t("Ошибка сохранения периода"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRiskySave = async (isYes) => {
    setIsConfirmRiskySaveOpen(false);
    if (!isYes) return;
    await savePeriod();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSave) return;

    if (student.studentId === -1) {
      setModalTitle(" ");
      setModalContent(
        t("Пожалуйста, сначала заполните профиль ученика и сохраните его"),
      );
      return;
    }

    if (isNewPeriod) {
      const hasSameDateRangePeriod = learningPeriods.some((period) => {
        const sameStart = period.start_date === periodStartDate;
        const sameEnd = (period.end_date || "") === (periodEndDate || "");
        return sameStart && sameEnd;
      });

      if (hasSameDateRangePeriod) {
        setModalTitle(t("Период уже существует"));
        setModalContent(
          t("У этого ученика уже есть период на выбранные даты. Вернитесь к существующему периоду и добавьте дополнительные уроки в него."),
        );
        return;
      }
    }

    if (!isNewPeriod) {
      const currentSnapshot = buildScheduleSnapshot({
        startDate: periodStartDate,
        endDate: periodEndDate || "",
        selectedWeekdays,
        selectedTimes,
      });
      const riskySummary = getRiskyChangesSummary(
        initialPeriodSnapshotRef.current,
        currentSnapshot,
        t,
      );
      if (riskySummary) {
        setRiskySaveSummary(riskySummary);
        setIsConfirmRiskySaveOpen(true);
        return;
      }
    }

    await savePeriod();
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (shouldBlockNavigation) {
        event.preventDefault();
        event.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [shouldBlockNavigation]);

  useBlocker(() => {
    if (shouldBlockNavigation) {
      return window.confirm(
        t("У вас есть несохраненные изменения. Хотите остаться на странице?"),
      );
    }
    return false;
  });

  return (
    <Fragment>
      <style>{dateInputStyles}</style>
      <ModalSimple
        isOpen={!!modalContent}
        onClose={() => {
          setModalContent(null);
          setModalTitle(null);
        }}
        h2={modalTitle || t("Ошибка")}
        p={modalContent}
      />
      <ModalQuestion
        isOpen={isConfirmDeletePeriodOpen}
        onClose={handleDeleteLearningPeriod}
        type="question"
        h2={t("Удалить период обучения?")}
        p={t("Период будет удалён. Будущие занятия этого периода отменятся. Прошедшие останутся в учёте.")}
        yesText={t("Удалить")}
        noText={t("Отмена")}
        isButtonRevers={false}
      />
      <ModalQuestion
        isOpen={isConfirmRemoveWeekdayOpen}
        onClose={handleConfirmRemoveWeekday}
        type="question"
        h2={t("Убрать день из расписания?")}
        p={
          weekdayToRemove
            ? t("{{weekday}} будет удалён из периода. Будущие занятия в этот день будут отменены после сохранения периода.", { weekday: t(weekDayFullName[weekdayToRemove] || weekdayToRemove) })
            : t("Этот день будет удалён из периода.")
        }
        yesText={t("Да, убрать")}
        noText={t("Отмена")}
        isButtonRevers={false}
      />
      <ModalQuestion
        isOpen={isConfirmRiskySaveOpen}
        onClose={handleConfirmRiskySave}
        type="question"
        h2={t("Применить изменения периода?")}
        p={
          riskySaveSummary ||
          t("После сохранения часть будущих занятий может быть изменена или отменена.")
        }
        yesText={t("Да, применить")}
        noText={t("Отмена")}
        isButtonRevers={false}
      />
      <form
        style={{
          ...styles.infoEditForm,
          width: "100%",
          boxSizing: "border-box",
        }}
        onSubmit={handleSubmit}
      >
        <ScheduleSection
          title={t("Периоды обучения")}
          titleHint={
            <ScheduleInfoHint
              title={t(HOLIDAYS_HINT_SHORT)}
              steps={HOLIDAYS_HINT_STEPS.map(t)}
            />
          }
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 10,
              padding: "10px 12px",
              borderRadius: 10,
              backgroundColor: "#F8F8F8",
              border: "1px solid #E8E8E8",
            }}
          >
            {isLearningPeriodsLoading && (
              <OnestNormalSmall style={{ color: color_grey_dark }}>
                {t("Загружаем периоды...")}
              </OnestNormalSmall>
            )}
            {!isLearningPeriodsLoading && learningPeriods.length === 0 && (
              <OnestNormalSmall
                style={{ color: color_black, alignSelf: "center" }}
              >
                {t("Периоды пока не добавлены")}
              </OnestNormalSmall>
            )}
            {!isLearningPeriodsLoading &&
              learningPeriods.map((period) => {
                const isSelected =
                  !isNewPeriod && editingPeriodId === period.id;
                return (
                  <div
                    key={period.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleSelectPeriod(period)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        handleSelectPeriod(period);
                      }
                    }}
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      flexDirection: isMobile ? "column" : "row",
                      justifyContent: "space-between",
                      border: `1px solid ${isSelected ? color_red_default : "#E8E8E8"}`,
                      borderRadius: 10,
                      padding: "8px 10px",
                      cursor: "pointer",
                      backgroundColor: isSelected ? "#F6F7F9" : color_white,
                    }}
                  >
                    <OnestNormalSmall
                      style={{
                        color: color_black,
                        lineHeight: "140%",
                        width: isMobile ? "100%" : "auto",
                        textAlign: isMobile ? "center" : "left",
                      }}
                    >
                      {moment(period.start_date).format("DD.MM.YYYY")} –{" "}
                      {period.end_date
                        ? moment(period.end_date).format("DD.MM.YYYY")
                        : t("без окончания")}
                    </OnestNormalSmall>
                    <button
                      type="button"
                      onClick={(event) =>
                        askDeleteLearningPeriod(period.id, event)
                      }
                      style={{
                        border: "none",
                        background: "transparent",
                        color: color_red_default,
                        cursor: "pointer",
                        fontFamily: "Onest",
                        padding: isMobile ? "8px 0" : 0,
                        minHeight: isMobile ? 44 : "auto",
                        alignSelf: isMobile ? "center" : "auto",
                      }}
                    >
                      {t("Удалить")}
                    </button>
                  </div>
                );
              })}
            {!isPeriodFormOpen && (
              <button
                type="button"
                onClick={() => {
                  if (isDirty) {
                    const confirmNew = window.confirm(
                      t("Есть несохранённые изменения. Начать новый период без сохранения?"),
                    );
                    if (!confirmNew) return;
                  }
                  startNewPeriod();
                  setIsPeriodFormOpen(true);
                }}
                style={{
                  border: "none",
                  borderRadius: 10,
                  backgroundColor: color_red_default,
                  color: color_white,
                  padding: "10px 12px",
                  cursor: "pointer",
                  width: "100%",
                  fontFamily: "Onest",
                  fontWeight: 500,
                  marginTop: 4,
                  boxShadow: "0 2px 8px rgba(233, 35, 57, 0.2)",
                }}
              >
                {t("Добавить период")}
              </button>
            )}
          </div>
        </ScheduleSection>

        <div
          style={{
            overflow: "hidden",
            maxHeight: isPeriodFormOpen ? "2000px" : 0,
            opacity: isPeriodFormOpen ? 1 : 0,
            transform: isPeriodFormOpen
              ? "translateY(0) scale(1)"
              : "translateY(-10px) scale(0.985)",
            transformOrigin: "top center",
            transition:
              "max-height 320ms cubic-bezier(0.22, 1, 0.36, 1), opacity 240ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)",
            willChange: "max-height, opacity, transform",
            pointerEvents: isPeriodFormOpen ? "auto" : "none",
          }}
        >
          <ScheduleSection
            title={editingTitle}
            titleHint={
              <ScheduleInfoHint
                title={t(TIME_HINT_TITLE)}
                steps={TIME_HINT_STEPS.map(t)}
              />
            }
          >
            <div
              style={{
                position: "relative",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <button
                type="button"
                onClick={handleClosePeriodForm}
                aria-label={t("Закрыть форму периода")}
                title={t("Закрыть")}
                style={{
                  position: "absolute",
                  top: -44,
                  right: 0,
                  border: "none",
                  background: "transparent",
                  padding: 0,
                  margin: 0,
                  width: 24,
                  height: 24,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M18 6L6 18"
                    stroke="#B1B1B1"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M6 6L18 18"
                    stroke="#B1B1B1"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <ScheduleDateInput
                inputRef={startDateInputRef}
                id="student-schedule-period-start-date"
                label={t("Начало")}
                value={periodStartDate}
                onChange={handleStartDateChange}
                isPickerOpenRef={isStartDatePickerOpenRef}
              />
              <ScheduleDateInput
                inputRef={endDateInputRef}
                id="student-schedule-period-end-date"
                label={t("Конец")}
                value={periodEndDate}
                onChange={handleEndDateChange}
                isPickerOpenRef={isEndDatePickerOpenRef}
              />
              <PeriodPresetDropdown
                activePresetId={activePresetId}
                onSelectPreset={handlePresetSelect}
              />
              <div>
                <OnestBoldSmall style={{ display: "block", marginBottom: 8 }}>
                  {t("Дни и время")}
                </OnestBoldSmall>
                <WeekdaySelectorButtons
                  selectedWeekdays={selectedWeekdays}
                  onToggleWeekday={handleToggleWeekday}
                />
                <WeekdayTimeSelector
                  selectedWeekdays={selectedWeekdays}
                  selectedTimes={selectedTimes}
                  onTimeChange={handleTimeChange}
                  conflictsByWeekday={conflictsByWeekday}
                  formatConflictMessage={(conflicts) => {
                    if (!conflicts?.length) return null;
                    const [first, ...rest] = conflicts;
                    return t("Слот занят: {{time}} ({{student}}){{suffix}}", {
                      time: first.formattedStart,
                      student: first.student_name,
                      suffix: rest.length
                        ? t(" и ещё {{count}} в периоде", { count: rest.length })
                        : "",
                    });
                  }}
                />
              </div>
            </div>
          </ScheduleSection>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: isDesktop ? "flex-end" : "stretch",
              gap: 8,
              marginTop: 16,
            }}
          >
            {saveHint && (
              <OnestNormalSmall
                style={{
                  color: color_grey_dark,
                  textAlign: isDesktop ? "right" : "center",
                }}
              >
                {saveHint}
              </OnestNormalSmall>
            )}
            {skippedPastTodayMessage && (
              <OnestNormalSmall
                style={{
                  color: color_grey_dark,
                  textAlign: isDesktop ? "right" : "center",
                }}
              >
                {skippedPastTodayMessage}
              </OnestNormalSmall>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: isDesktop ? "flex-end" : "center",
                gap: 16,
                alignItems: "center",
                width: "100%",
              }}
            >
              {isLoading && (
                <OnestNormalDefault>{t("Сохранение...")}</OnestNormalDefault>
              )}
              {isLoading && (
                <ClipLoader
                  color={color_red_default}
                  size={35}
                  loading={isLoading}
                />
              )}
              <Button
                width={isDesktop ? 180 : "100%"}
                height={40}
                backgroundColor={canSave ? color_red_default : color_grey_light}
                disabled={!canSave}
                color={color_white}
                type="submit"
                style={{ borderWidth: 0 }}
              >
                <OnestNormalDefault>{t("Сохранить")}</OnestNormalDefault>
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Fragment>
  );
};
